import { useId } from "react";

const svgProps = {
  "aria-hidden": true,
  preserveAspectRatio: "xMidYMid meet",
  viewBox: "0 0 300 112",
  xmlns: "http://www.w3.org/2000/svg",
} as const;

export function AssetsIllustration() {
  const gradientId = useId();

  return (
    <svg {...svgProps} className="size-full">
      <defs>
        <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#1a1a1a" />
          <stop offset=".55" stopColor="#c2410c" />
          <stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
      </defs>
      <rect
        fill="#fff"
        height="26"
        rx="7"
        stroke="#E4E7EC"
        width="52"
        x="22"
        y="14"
      />
      <text
        fill="#000"
        fontFamily="Inter,sans-serif"
        fontSize="15"
        fontStyle="italic"
        fontWeight="800"
        textAnchor="middle"
        x="48"
        y="32"
      >
        lel
      </text>
      <rect
        fill={`url(#${gradientId})`}
        height="26"
        rx="7"
        width="52"
        x="22"
        y="43"
      />
      <rect
        fill="#fff"
        height="26"
        rx="7"
        stroke="#E4E7EC"
        width="52"
        x="22"
        y="72"
      />
      <text
        fill="#344054"
        fontFamily="Inter,sans-serif"
        fontSize="14"
        fontWeight="600"
        textAnchor="middle"
        x="48"
        y="90"
      >
        Aa
      </text>
      <path
        d="M84 56 H126"
        stroke="#6434DE"
        strokeDasharray="4 4"
        strokeLinecap="round"
        strokeWidth="2"
      />
      <path
        d="M121 51 l6 5 -6 5"
        fill="none"
        stroke="#6434DE"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <rect
        fill="#fff"
        height="92"
        rx="9"
        stroke="#D0D5DD"
        width="144"
        x="138"
        y="10"
      />
      <rect fill="#000" height="14" rx="4" width="30" x="150" y="22" />
      <text
        fill="#fff"
        fontFamily="Inter,sans-serif"
        fontSize="10"
        fontStyle="italic"
        fontWeight="800"
        textAnchor="middle"
        x="165"
        y="33"
      >
        lel
      </text>
      <rect fill="#D0D5DD" height="6" rx="3" width="64" x="150" y="46" />
      <rect fill="#E4E7EC" height="6" rx="3" width="48" x="150" y="58" />
      <rect fill="#E4E7EC" height="6" rx="3" width="56" x="150" y="70" />
      <rect fill="#101828" height="10" rx="5" width="40" x="156" y="84" />
      <rect fill="#C8261C" height="66" rx="8" width="36" x="232" y="24" />
      <rect
        fill="#fff"
        height="22"
        opacity=".9"
        rx="3"
        width="24"
        x="238"
        y="46"
      />
      <rect fill="#101828" height="9" rx="3" width="28" x="236" y="18" />
    </svg>
  );
}

type PosterProps = { fill: string; transform: string; width: number };

function Poster({ fill, transform, width }: PosterProps) {
  const half = width / 2;
  const textX = -half + 12;

  return (
    <g transform={transform}>
      <rect
        fill={fill}
        height="80"
        rx="8"
        stroke="#fff"
        strokeWidth="3"
        width={width}
        x={-half}
        y="-40"
      />
      <rect
        fill="#fff"
        height="5"
        opacity=".8"
        rx="2.5"
        width={width - 24}
        x={textX}
        y="8"
      />
      <rect
        fill="#fff"
        height="5"
        opacity=".5"
        rx="2.5"
        width={width - 36}
        x={textX}
        y="18"
      />
    </g>
  );
}

export function InspirationIllustration() {
  const id = useId();
  const dark = `${id}-dark`;
  const blue = `${id}-blue`;
  const red = `${id}-red`;

  return (
    <svg {...svgProps} className="size-full">
      <defs>
        <linearGradient id={dark} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#0b0b0b" />
          <stop offset="1" stopColor="#6b6b6b" />
        </linearGradient>
        <linearGradient id={blue} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#1d4ed8" />
          <stop offset=".6" stopColor="#2563eb" />
          <stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
        <linearGradient id={red} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#7f1d1d" />
          <stop offset=".6" stopColor="#dc2626" />
          <stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
      </defs>
      <Poster
        fill={`url(#${dark})`}
        transform="translate(106 58) rotate(-10)"
        width={60}
      />
      <Poster
        fill={`url(#${red})`}
        transform="translate(194 58) rotate(10)"
        width={60}
      />
      <g transform="translate(150 54)">
        <rect
          fill={`url(#${blue})`}
          height="84"
          rx="8"
          stroke="#fff"
          strokeWidth="3"
          width="64"
          x="-32"
          y="-42"
        />
        <rect
          fill="#fff"
          height="5"
          opacity=".85"
          rx="2.5"
          width="40"
          x="-20"
          y="10"
        />
        <rect
          fill="#fff"
          height="5"
          opacity=".55"
          rx="2.5"
          width="26"
          x="-20"
          y="20"
        />
      </g>
      <path
        d="M246 22 l3 8 8 3 -8 3 -3 8 -3 -8 -8 -3 8 -3z"
        fill="none"
        stroke="#6434DE"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        d="M44 80 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2z"
        fill="none"
        stroke="#6434DE"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}
